/**
 * Standalone read-only message viewer for restricted students (free / applied).
 * Triggered from the notification bell when an admin/instructor messages them.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { EnvelopeOpen, ArrowLeft } from "@phosphor-icons/react";

export default function InboxMessagePage() {
  const { id } = useParams();
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/messages/${id}`).then(r => { setMsg(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [id]);

  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-3xl" data-testid="inbox-message-page">
        <Link to="/dashboard" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-4">
          <ArrowLeft size={14} /> Back to Dashboard
        </Link>
        {loading ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : !msg ? (
          <p className="text-sm text-slate-500" data-testid="inbox-missing">This message no longer exists.</p>
        ) : (
          <Card className="rounded-none border-slate-200">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-3 pb-3 border-b border-slate-200">
                <EnvelopeOpen size={24} className="text-blue-800" weight="fill" />
                <div>
                  <p className="text-xs text-slate-500">From {msg.sender_name}</p>
                  <p className="text-[10px] text-slate-400">{msg.created_at ? new Date(msg.created_at).toLocaleString() : ""}</p>
                </div>
              </div>
              <h1 className="text-xl font-bold text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="inbox-subject">{msg.subject || "(No subject)"}</h1>
              <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="inbox-content">{msg.content}</div>
              {msg.attachment_url && (
                <div className="pt-3 border-t border-slate-200">
                  <a href={msg.attachment_url} target="_blank" rel="noreferrer">
                    <Button variant="outline" className="rounded-none text-sm" data-testid="inbox-attachment-btn">
                      Download attachment{msg.attachment_name ? `: ${msg.attachment_name}` : ""}
                    </Button>
                  </a>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
